import axios from '../config/axiosConfig';
import { CountryItem, Option, StateItem } from '../interfaces/CarWashInterface';
import { CompanyApi, StateApi } from './apiUrls';

export const DEFAULT_COUNTRY_CODE = 'US';
export const DEFAULT_STATE_LABEL = 'State';
export const DEFAULT_POSTAL_LABEL = 'ZIP Code';

type PostalCodeRule = {
    format: RegExp;
    allowedInput: RegExp;
    disallowedChars: RegExp;
    maxLength: number;
};

const POSTAL_CODE_RULES: Record<string, PostalCodeRule> = {
    US: {
        format: /^\d{5}(-\d{4})?$/,
        allowedInput: /^[\d-]*$/,
        disallowedChars: /[^\d-]/g,
        maxLength: 10,
    },
    CA: {
        format: /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d$/i,
        allowedInput: /^[A-Za-z0-9 ]*$/,
        disallowedChars: /[^A-Za-z0-9 ]/g,
        maxLength: 7,
    },
    AU: {
        format: /^\d{4}$/,
        allowedInput: /^\d*$/,
        disallowedChars: /\D/g,
        maxLength: 4,
    },
};

const DEFAULT_POSTAL_CODE_RULE: PostalCodeRule = {
    format: /^[A-Za-z0-9][A-Za-z0-9 -]{2,9}$/,
    allowedInput: /^[A-Za-z0-9 -]*$/,
    disallowedChars: /[^A-Za-z0-9 -]/g,
    maxLength: 10,
};

export const getPostalCodeRule = (countryCode?: string | null) => {
    const code = normalizeCountryCode(countryCode);
    return POSTAL_CODE_RULES[code] || DEFAULT_POSTAL_CODE_RULE;
};

export const getPostalCodeMaxLength = (countryCode?: string | null) =>
    getPostalCodeRule(countryCode).maxLength;

export const isAllowedPostalCodeInput = (value: string, countryCode?: string | null) =>
    getPostalCodeRule(countryCode).allowedInput.test(value);

export const sanitizePostalCodeInput = (value: string, countryCode?: string | null) =>
    value.replace(getPostalCodeRule(countryCode).disallowedChars, '');

export const normalizePostalCodeInput = (value: string, countryCode?: string | null) => {
    const sanitized = sanitizePostalCodeInput(value, countryCode);
    return normalizeCountryCode(countryCode) === 'CA' ? sanitized.toUpperCase() : sanitized;
};

export const isValidPostalCode = (value?: string | null, countryCode?: string | null) => {
    if (!value?.trim()) {
        return false;
    }
    return getPostalCodeRule(countryCode).format.test(value.trim());
};

const COUNTRY_DISPLAY_NAMES: Record<string, string> = {
    US: 'United States',
    CA: 'Canada',
    AU: 'Australia',
};

export const getCountryDisplayName = (countryCode?: string | null) => {
    if (!countryCode?.trim()) {
        return '';
    }
    const code = normalizeCountryCode(countryCode);
    return COUNTRY_DISPLAY_NAMES[code] || countryCode;
};

export const isUnitedStates = (countryCode?: string | null) =>
    Boolean(countryCode?.trim()) && normalizeCountryCode(countryCode) === 'US';

export const fetchCompanyAddressDetails = async (isOwner: boolean, carwashId?: number | string) => {
    const { data } = isOwner
        ? await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS)
        : await axios.get(`${CompanyApi.GET_COMPANY_ADDRESS}${carwashId || ''}`);
    if (data?.error === 0) {
        return {
            ...data.data,
            countryCode: getCountryCodeFromRecord(data.data),
            state: data.data?.state || '',
        };
    }
    return null;
};

export const resolveLocationCountryState = async (
    locationData: { hasDefaultAddress?: boolean; country?: string | null; state?: string | null } | undefined,
    isOwner: boolean,
    carwashId?: number | string
) => {
    if (locationData?.hasDefaultAddress) {
        const company = await fetchCompanyAddressDetails(isOwner, carwashId);
        if (company) {
            return {
                countryCode: company.countryCode,
                stateCode: company.state || locationData?.state || '',
            };
        }
    }
    return {
        countryCode: locationData?.country,
        stateCode: locationData?.state,
    };
};

export const getCountryCodeFromRecord = (
    record?: {
        country?: string | null;
        countryCode?: string | null;
        CountryCode?: string | null;
    } | null,
    options?: { fallback?: boolean }
) => {
    const raw = record?.CountryCode || record?.countryCode || record?.country;
    if (!raw) {
        return options?.fallback === false ? '' : DEFAULT_COUNTRY_CODE;
    }
    return normalizeCountryCode(raw);
};

export const normalizeCountryCode = (code?: string | null) => {
    if (!code) {
        return DEFAULT_COUNTRY_CODE;
    }
    const upper = code.toUpperCase();
    if (upper === 'USA' || upper === 'UNITED STATES') {
        return 'US';
    }
    if (upper === 'CAN' || upper === 'CANADA') {
        return 'CA';
    }
    if (upper === 'AUS' || upper === 'AUSTRALIA') {
        return 'AU';
    }
    return upper;
};

export const getSelectedOptions = (options: Option[], value?: string | null) => {
    if (!value) {
        return [];
    }
    const normalized = String(value).trim().toUpperCase();
    return (options || []).filter(option =>
        String(option.value || '').trim().toUpperCase() === normalized ||
        String(option.label || '').trim().toUpperCase() === normalized
    );
};

export const mapCountryOptions = (countries: CountryItem[]): Option[] => {
    return (countries || [])
        .map((item) => {
            const value = (item.countryCode || '').toUpperCase();
            return {
                value,
                label: COUNTRY_DISPLAY_NAMES[value] || value,
            };
        })
        .filter(option => option.value);
};

export const getCountryFieldLabels = (countries: CountryItem[], countryCode?: string | null) => {
    const selectedCode = normalizeCountryCode(countryCode);
    const selectedCountry = (countries || []).find(
        (item) => (item.countryCode || '').toUpperCase() === selectedCode
    );
    return {
        stateLabel: selectedCountry?.stateLabel || DEFAULT_STATE_LABEL,
        postalLabel: selectedCountry?.postalLabel || DEFAULT_POSTAL_LABEL,
    };
};

export const fetchSupportedCountries = async (): Promise<CountryItem[]> => {
    try {
        const { data } = await axios.get(CompanyApi.GET_SUPPORTED_COUNTRIES);
        if (data?.error === 0) {
            return data?.data?.countries || [];
        }
        throw new Error(data?.message || 'Failed to load countries');
    } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
    }
};

export const fetchStatesByCountry = async (countryCode: string): Promise<Option[]> => {
    if (!countryCode) {
        return [];
    }
    try {
        const { data } = await axios.get(StateApi.GET_STATE + countryCode);
        if (data?.error === 0) {
            const states_list: StateItem[] = data?.data || [];
            return states_list.map(item => ({
                label: item.StateName,
                value: item.StateCode,
            }));
        }
        throw new Error(data?.message || 'Failed to load states');
    } catch (error: any) {
        throw new Error(error.response?.data?.message || error.message);
    }
};
